import { useState } from 'react';
import { Eye, EyeOff, Lock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth-store';

interface ChangePasswordModalProps {
  /** Forced mode: shown right after login when must_change_password=true.
   *  No close button — the user can only save a new password or sign out. */
  forced?: boolean;
  onClose?: () => void;
}

export function ChangePasswordModal({ forced = false, onClose }: ChangePasswordModalProps) {
  const changePassword = useAuthStore((s) => s.changePassword);
  const signOut = useAuthStore((s) => s.signOut);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSave() {
    setError(null);
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      await changePassword(password);
      setDone(true);
      if (!forced) {
        setTimeout(() => onClose?.(), 1200);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo actualizar la contraseña.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-background border shadow-2xl p-6">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
              <Lock className="h-5 w-5 text-amber-500" />
            </div>
            <h2 className="text-lg font-bold">
              {forced ? 'Crea tu nueva contraseña' : 'Cambiar contraseña'}
            </h2>
          </div>
          {!forced && (
            <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {forced
            ? 'Estás usando la contraseña temporal asignada al crear tu cuenta. Por seguridad debes definir una propia antes de continuar.'
            : 'Define una nueva contraseña para tu cuenta.'}
        </p>

        {done ? (
          <div className="rounded-lg bg-green-500/10 text-green-600 text-sm p-3 text-center font-medium">
            Contraseña actualizada correctamente.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Nueva contraseña (mín. 8 caracteres)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="relative">
              <Input
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirmar contraseña"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="pr-10"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {error && (
              <p className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
            )}

            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar y continuar'}
            </Button>

            {forced && (
              <button
                type="button"
                onClick={() => signOut()}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Cerrar sesión
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
